import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { Column } from 'react-table';
import moment from 'moment';
import StatusCard from './StatusCard';
import ServiceGridTable from './ServiceGridTable';
import { workHistoryCutoff } from './serviceGridUtils';
import ServiceSearchBar from './ServiceSearchBar';
import PrintUpdatedListModal from './PrintUpdatedListModal';
import {
    InspectionStatusData,
    MaintenanceStatusData,
    WorkHistoryData
} from './mockServiceGridData';
import { svgIcons } from '../../constants/svgIcons';
import {
    getDashboardAnalytics,
    getDashboardInspectionStatus,
    getDashboardMaintenanceActions,
    getDashboardWorkHistory
} from '../../actions/dashboardAnalyticsActions';
import {
    IdashboardAnalyticsData,
    IproductInfo,
    Ifacility,
    IcloudDocument
} from '../../models';
import { selectSelectedFacilityWithBuildings } from '../../reducers/facilitiesReducer';
import { selectIsLoading } from '../../reducers/commonReducers';
import { getFacility } from '../../actions/manageLocationActions';
import { setTableFilter } from '../../actions/manageWorkOrderActions';
import { runCodeDeficiencyReport } from '../../actions/manageReportActions';
import { getProductInfo } from '../../actions/manageInventoryActions';
import { constants, jobTypeOptions } from '../../constants/constants';
import {
    statusNameToEnumMap,
    MaintenanceActionSource,
    MaintenanceTriggerStatus,
    workOrderTabEnum,
    jobTypesIdEnumInverse
} from '../../models-enums';
import { applyStatusLabelOverrides } from '../../helpers/statusLabelHelper';
import { getCloudDocument } from '../../actions/manageDocumentActions';

interface ServiceGridProps {
    selectedFacilityID: string;
    selectedFacilityName: string;
    selectedFacility: Ifacility;
    dashboardData: IdashboardAnalyticsData;
    inspectionStatus: any;
    maintenanceActions: any;
    workHistory: any;
    productInfo: IproductInfo;
    isLoading: boolean;
    getDashboardAnalytics: (
        buildingID?: string,
        floorID?: string,
        locationID?: string
    ) => void;
    getDashboardInspectionStatus: (
        facilityID: string,
        buildingID?: string,
        floorID?: string,
        locationID?: string
    ) => void;
    getDashboardMaintenanceActions: (
        facilityID: string,
        buildingID?: string,
        floorID?: string,
        locationID?: string
    ) => void;
    getDashboardWorkHistory: (facilityID: string, cutoffDate?: string) => void;
    getFacility: (facilityID: string) => void;
    setTableFilter: typeof setTableFilter;
    getCloudDocument: typeof getCloudDocument;
    runCodeDeficiencyReport: typeof runCodeDeficiencyReport;
    getProductInfo: typeof getProductInfo;
}

const ServiceGrid: React.FC<ServiceGridProps> = ({
    selectedFacilityID,
    selectedFacilityName,
    selectedFacility,
    dashboardData,
    inspectionStatus,
    maintenanceActions,
    workHistory,
    productInfo,
    isLoading,
    getDashboardAnalytics: fetchDashboardAnalytics,
    getDashboardInspectionStatus: fetchInspectionStatus,
    getDashboardMaintenanceActions: fetchMaintenanceActions,
    getDashboardWorkHistory: fetchWorkHistory,
    getFacility: fetchFacilityHierarchy,
    setTableFilter: setWorkOrderTableFilter,
    getCloudDocument: downloadCloudDocument,
    runCodeDeficiencyReport: runReport,
    getProductInfo: fetchProductInfo
}) => {
    const { t } = useTranslation();
    const history = useHistory();

    // Track if we're fetching data for a new facility
    const [isFetchingNewFacility, setIsFetchingNewFacility] = useState(false);
    const [lastFetchedFacilityID, setLastFetchedFacilityID] = useState('');
    const [showPrintModal, setShowPrintModal] = useState(false);
    const [isFilteringData, setIsFilteringData] = useState(false);

    // the category filter reads from product info, which nothing else on this screen loads
    useEffect(() => {
        fetchProductInfo();
    }, []);

    // Fetch data when component mounts or facility changes
    useEffect(() => {
        if (selectedFacilityID) {
            // Check if this is a new facility
            if (selectedFacilityID !== lastFetchedFacilityID) {
                setIsFetchingNewFacility(true);
                setLastFetchedFacilityID(selectedFacilityID);

                // Fetch facility hierarchy (buildings/floors/locations)
                fetchFacilityHierarchy(selectedFacilityID);
                // Fetch dashboard analytics for StatusCard
                fetchDashboardAnalytics();
                // Fetch all three datasets only when facility changes (without filters on initial load)
                fetchInspectionStatus(selectedFacilityID);
                fetchMaintenanceActions(selectedFacilityID);
            }
        }
    }, [
        selectedFacilityID,
        lastFetchedFacilityID,
        fetchFacilityHierarchy,
        fetchDashboardAnalytics,
        fetchInspectionStatus,
        fetchMaintenanceActions
    ]);

    // Clear fetching state when data arrives
    useEffect(() => {
        if (isFetchingNewFacility && !isLoading) {
            setIsFetchingNewFacility(false);
        }
        if (isFilteringData && !isLoading) {
            setIsFilteringData(false);
        }
    }, [isLoading, isFetchingNewFacility, isFilteringData]);

    // State for search bar filters
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Work history covers the past year by default; a start date before that asks the
    // API to widen the window, and the result is the full set the results count divides by
    const workHistoryCutoffDate = workHistoryCutoff(startDate);
    useEffect(() => {
        if (selectedFacilityID) {
            fetchWorkHistory(selectedFacilityID, workHistoryCutoffDate);
        }
    }, [selectedFacilityID, workHistoryCutoffDate, fetchWorkHistory]);

    // State for equipment-style filters
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [selectedStatus, setSelectedStatus] = useState('All');
    const [selectedBuilding, setSelectedBuilding] = useState('All');
    const [selectedFloor, setSelectedFloor] = useState('All');
    const [selectedZone, setSelectedZone] = useState('All');

    // State for work history filter
    const [selectedJobType, setSelectedJobType] = useState('All');

    // State for collapse functionality
    const [isInspectionCollapsed, setIsInspectionCollapsed] = useState(false);
    const [isMaintenanceCollapsed, setIsMaintenanceCollapsed] = useState(false);
    const [isWorkHistoryCollapsed, setIsWorkHistoryCollapsed] = useState(false);

    // Refetch data when location filters change
    useEffect(() => {
        // Only refetch if we have a facility and it's not a new facility load
        if (
            selectedFacilityID &&
            !isFetchingNewFacility &&
            selectedFacilityID === lastFetchedFacilityID
        ) {
            setIsFilteringData(true);

            const buildingIDParam =
                selectedBuilding !== 'All' ? selectedBuilding : undefined;
            const floorIDParam =
                selectedFloor !== 'All' ? selectedFloor : undefined;
            const locationIDParam =
                selectedZone !== 'All' ? selectedZone : undefined;

            // Refetch dashboard analytics with location filters (for Inspection Status chart)
            fetchDashboardAnalytics(
                buildingIDParam,
                floorIDParam,
                locationIDParam
            );

            // Refetch inspection status with location filters (for table data)
            fetchInspectionStatus(
                selectedFacilityID,
                buildingIDParam,
                floorIDParam,
                locationIDParam
            );

            // Refetch maintenance actions with location filters
            fetchMaintenanceActions(
                selectedFacilityID,
                buildingIDParam,
                floorIDParam,
                locationIDParam
            );
        }
    }, [
        selectedBuilding,
        selectedFloor,
        selectedZone,
        selectedFacilityID,
        isFetchingNewFacility,
        lastFetchedFacilityID,
        fetchDashboardAnalytics,
        fetchInspectionStatus,
        fetchMaintenanceActions
    ]);

    // Helper function to format date from ISO to MM/YYYY
    const formatDate = (isoDate: string): string => {
        const date = new Date(isoDate);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${year}`;
    };

    // Generate building options from facility hierarchy
    const buildingOptions = useMemo(() => {
        console.log('ServiceGrid - selectedFacility:', selectedFacility);

        if (!selectedFacility || !selectedFacility.buildings) {
            console.log('ServiceGrid - No facility or buildings found');
            return [{ value: 'All', label: 'Bldg: All' }];
        }

        console.log(
            'ServiceGrid - Buildings data:',
            selectedFacility.buildings
        );

        // Convert buildings to options format and sort by name
        const options = selectedFacility.buildings
            .map(building => ({
                value: building.id,
                label: building.name
            }))
            .sort((a, b) => a.label.localeCompare(b.label));

        // Add "All" option at the beginning
        const finalOptions = [{ value: 'All', label: 'Bldg: All' }, ...options];
        console.log('ServiceGrid - Building options:', finalOptions);
        return finalOptions;
    }, [selectedFacility]);

    // Generate floor options from facility hierarchy (filtered by selected building)
    const floorOptions = useMemo(() => {
        console.log('ServiceGrid - selectedBuilding:', selectedBuilding);

        if (!selectedFacility || !selectedFacility.buildings) {
            return [{ value: 'All', label: 'Floor: No options' }];
        }

        // Only show floor options if a specific building is selected
        if (selectedBuilding === 'All') {
            return [{ value: 'All', label: 'Floor: No options' }];
        }

        // Find the selected building
        const building = selectedFacility.buildings.find(
            b => b.id === selectedBuilding
        );

        console.log('ServiceGrid - Found building:', building);

        if (!building || !building.floors) {
            console.log('ServiceGrid - No building or floors found');
            return [{ value: 'All', label: 'Floor: No options' }];
        }

        console.log('ServiceGrid - Floors data:', building.floors);

        // Convert floors to options format and sort by name
        const options = building.floors
            .map(floor => ({
                value: floor.id,
                label: floor.name
            }))
            .sort((a, b) => a.label.localeCompare(b.label));

        // Add "All" option at the beginning
        const finalOptions = [
            { value: 'All', label: 'Floor: All' },
            ...options
        ];
        console.log('ServiceGrid - Floor options:', finalOptions);
        return finalOptions;
    }, [selectedFacility, selectedBuilding]);

    // Generate zone (location) options from facility hierarchy (filtered by selected building and floor)
    const zoneOptions = useMemo(() => {
        console.log('ServiceGrid - selectedFloor:', selectedFloor);

        if (!selectedFacility || !selectedFacility.buildings) {
            return [{ value: 'All', label: 'Zone: No options' }];
        }

        // Only show zone options if a specific building and floor are selected
        if (selectedBuilding === 'All' || selectedFloor === 'All') {
            return [{ value: 'All', label: 'Zone: No options' }];
        }

        // Find the selected building
        const building = selectedFacility.buildings.find(
            b => b.id === selectedBuilding
        );

        if (!building || !building.floors) {
            return [{ value: 'All', label: 'Zone: No options' }];
        }

        // Find the selected floor
        const floor = building.floors.find(f => f.id === selectedFloor);

        console.log('ServiceGrid - Found floor:', floor);

        if (!floor || !floor.locations) {
            console.log('ServiceGrid - No floor or locations found');
            return [{ value: 'All', label: 'Zone: No options' }];
        }

        console.log('ServiceGrid - Locations data:', floor.locations);

        // Convert locations to options format and sort by name
        const options = floor.locations
            .map(location => ({
                value: location.id,
                label: location.name
            }))
            .sort((a, b) => a.label.localeCompare(b.label));

        // Add "All" option at the beginning
        const finalOptions = [{ value: 'All', label: 'Zone: All' }, ...options];
        console.log('ServiceGrid - Zone options:', finalOptions);
        return finalOptions;
    }, [selectedFacility, selectedBuilding, selectedFloor]);

    // Transform inspection status data from API to component format
    const transformedInspectionData = useMemo(() => {
        // Extract StatusCard data from dashboardData.inspection
        let statusBar: any[] = [];
        let totalAssets = 0;

        if (
            dashboardData &&
            dashboardData.inspection &&
            dashboardData.inspection.length > 0
        ) {
            // Find the "All" category or first available inspection data
            let selectedInspection = dashboardData.inspection.find(
                item => item.mainCategoryID === constants.ALL_CATEGORY_ID
            );

            // If no "ALL" category exists, fall back to first category with data
            if (!selectedInspection || selectedInspection.total === 0) {
                selectedInspection = dashboardData.inspection.find(
                    item => item.total > 0
                );
            }

            if (selectedInspection && selectedInspection.total > 0) {
                const { data, total } = selectedInspection;
                totalAssets = total;

                // Transform data array to StatusBarItem format
                const allStatusItems = data.map((item: any) => {
                    const statusNameLower = item.name?.toLowerCase() || '';
                    const quantity = Math.round((item.y / 100) * total);

                    // Convert to camelCase for translation key
                    const translationKey = item.name
                        ? item.name.charAt(0).toLowerCase() + item.name.slice(1)
                        : statusNameLower;

                    // Translate the status name
                    const translatedLabel = t(
                        `dashboardAnalytics:${translationKey}`
                    );

                    // Get color from constants lookup
                    const color =
                        constants.installBaseStatusColorLookup[
                            translatedLabel as keyof typeof constants.installBaseStatusColorLookup
                        ] || constants.colors.greyBlue;

                    // Apply display label overrides
                    const displayLabel = applyStatusLabelOverrides(
                        translatedLabel
                    );

                    return {
                        label: displayLabel,
                        value: quantity,
                        color,
                        className: statusNameLower.replace(/\s+/g, '-'),
                        statusEnum: statusNameToEnumMap[statusNameLower]
                    };
                });

                // Only include items with value > 0
                statusBar = allStatusItems.filter(
                    (item: any) => item.value > 0
                );
            }
        }

        // Extract table data from inspectionStatus
        let tableData: InspectionStatusData[] = [];

        if (inspectionStatus) {
            let jobsArray = null;

            // Handle if API returns object with jobs array
            if (
                inspectionStatus &&
                typeof inspectionStatus === 'object' &&
                !Array.isArray(inspectionStatus)
            ) {
                jobsArray =
                    inspectionStatus.jobs ||
                    inspectionStatus.data ||
                    inspectionStatus.tableData;
            } else if (Array.isArray(inspectionStatus)) {
                jobsArray = inspectionStatus;
            }

            if (jobsArray && Array.isArray(jobsArray)) {
                tableData = jobsArray.map((item: any) => ({
                    jobName: item.jobNumber || 'N/A',
                    facility: selectedFacilityName || 'Unknown Facility',
                    date: formatDate(item.startDate),
                    rawDate: item.startDate, // Preserve raw ISO date for filtering
                    inspected: item.inspected,
                    pass: item.pass,
                    report: item.jobID,
                    documentID: item.documentID,
                    versionID: item.versionID
                }));
            }
        }

        return {
            statusBar,
            totalAssets,
            tableData
        };
    }, [dashboardData, inspectionStatus, selectedFacilityName, t]);

    // Filter inspection data based on selected status
    const filteredInspectionData = useMemo(() => {
        if (selectedStatus === 'All') {
            return transformedInspectionData;
        }

        // Filter statusBar to only include the selected status
        const filteredStatusBar = transformedInspectionData.statusBar.filter(
            item => {
                // Match by the status value (lowercase version)
                // The statusBar items have translated labels, but we need to match by the original status name
                // We'll need to compare using the translated + override label
                const translatedLabel = t(
                    `dashboardAnalytics:${selectedStatus}`
                );
                const displayLabel = applyStatusLabelOverrides(translatedLabel);
                return item.label === displayLabel;
            }
        );

        // Calculate filtered total assets (sum of filtered status values)
        const filteredTotalAssets = filteredStatusBar.reduce(
            (sum, item) => sum + item.value,
            0
        );

        return {
            statusBar: filteredStatusBar,
            totalAssets: filteredTotalAssets,
            tableData: transformedInspectionData.tableData
        };
    }, [transformedInspectionData, selectedStatus, t]);

    // Transform maintenance triggers data from API to component format
    const transformedMaintenanceData = useMemo(() => {
        const empty = {
            statusBar: [] as any[],
            totalActions: 0,
            tableData: [] as MaintenanceStatusData[]
        };

        if (!maintenanceActions || !Array.isArray(maintenanceActions)) {
            return empty;
        }

        const mapStatusToString = (statusEnum: number): string => {
            switch (statusEnum) {
                case MaintenanceTriggerStatus.Ok:
                    return 'ok';
                case MaintenanceTriggerStatus.DueSoon:
                    return 'due-soon';
                case MaintenanceTriggerStatus.Due:
                    return 'due';
                case MaintenanceTriggerStatus.Snoozed:
                    return 'snoozed';
                default:
                    return 'ok';
            }
        };

        const maintenanceTypeLabel = (typeEnum: number): string => {
            const match = constants.maintenanceTypeOptions.find(
                (o: any) => o.value === typeEnum
            );
            return match ? match.label : '';
        };

        // Service Plan rows carry a SAP visit letter instead of a maintenance type, shown using
        // SAP's own wording. A-F map to service intervals (quarterly, six month, annual, two year,
        // three year, five year) but G/I/S have no definition on our side, so the code is shown
        // rather than translating only some of them.
        const visitCodeLabel = (visitCode: string): string =>
            visitCode ? `Visit Type ${visitCode}` : t('nsJob:servicePlan');

        const actionLabel = (row: any): string =>
            row.source === MaintenanceActionSource.ServicePlan
                ? visitCodeLabel(row.visitCode)
                : maintenanceTypeLabel(row.maintenanceType);

        let okCount = 0;
        let dueSoonCount = 0;
        let dueCount = 0;
        // snoozed rows are excluded from status-bar counts

        const tableData: MaintenanceStatusData[] = maintenanceActions.map(
            (trigger: any) => {
                const status = mapStatusToString(trigger.status);

                if (status === 'ok') okCount++;
                else if (status === 'due-soon') dueSoonCount++;
                else if (status === 'due') dueCount++;

                return {
                    facility: selectedFacilityName || 'Unknown Facility',
                    asset: trigger.assetDisplayName || 'N/A',
                    maintenanceType: actionLabel(trigger),
                    status,
                    dueDate: formatDate(trigger.nextDueDate),
                    rawDueDate: trigger.nextDueDate
                };
            }
        );

        const statusBar = [
            {
                label: 'OK',
                value: okCount,
                color: '#4CAF50',
                className: 'status-ok'
            },
            {
                label: 'Due Soon',
                value: dueSoonCount,
                color: '#FF9800',
                className: 'status-due-soon'
            },
            {
                label: 'Due',
                value: dueCount,
                color: '#F44336',
                className: 'status-due'
            }
        ];

        return {
            statusBar,
            totalActions: okCount + dueSoonCount + dueCount,
            tableData
        };
    }, [maintenanceActions, selectedFacilityName, t]);

    // Transform work history data from API to component format
    const transformedWorkHistoryData = useMemo(() => {
        if (!workHistory || !Array.isArray(workHistory)) {
            return {
                tableData: [] as WorkHistoryData[]
            };
        }

        const tableData: WorkHistoryData[] = workHistory.map((job: any) => {
            const rawJobType: string =
                jobTypesIdEnumInverse[
                    job.jobTypeID as keyof typeof jobTypesIdEnumInverse
                ] || 'Unknown';

            // Translate the job type using the nsJob namespace
            const translatedJobType =
                rawJobType !== 'Unknown' ? t(`nsJob:${rawJobType}`) : 'Unknown';

            return {
                jobName: job.jobNumber || '',
                workOrderNumber:
                    job.workOrderNumbers && Array.isArray(job.workOrderNumbers)
                        ? job.workOrderNumbers.join(', ')
                        : '',
                date: job.startDate
                    ? new Date(job.startDate).toLocaleDateString('en-US', {
                          month: '2-digit',
                          day: '2-digit',
                          year: 'numeric'
                      })
                    : '',
                rawDate: job.startDate, // Preserve raw ISO date for filtering
                type: translatedJobType,
                report: '', // Placeholder - report viewing will be implemented in button handler
                documentID: job.documentID,
                versionID: job.versionID
            };
        });

        return {
            tableData
        };
        // eslint-disable-next-line
    }, [workHistory, selectedFacilityName, t]);

    // Generate job type filter options from existing jobTypeOptions constant
    const jobTypeFilterOptions = useMemo(() => {
        // Get translated labels from jobTypeOptions and sort alphabetically
        const translatedLabels = jobTypeOptions.map(option =>
            t(`nsJob:${option.label}`)
        );
        return translatedLabels.sort();
    }, [t]);

    // Apply date range filtering to inspection table data
    const filteredInspectionTableData = useMemo(() => {
        if (!startDate && !endDate) {
            return filteredInspectionData.tableData;
        }

        return filteredInspectionData.tableData.filter((item: any) => {
            if (!item.rawDate) return false;

            const itemDate = new Date(item.rawDate);
            const start = startDate ? new Date(startDate) : null;
            const end = endDate ? new Date(endDate) : null;

            if (start && end) {
                return itemDate >= start && itemDate <= end;
            } else if (start) {
                return itemDate >= start;
            } else if (end) {
                return itemDate <= end;
            }
            return true;
        });
    }, [filteredInspectionData.tableData, startDate, endDate]);

    // Apply date range filtering to maintenance table data
    const filteredMaintenanceTableData = useMemo(() => {
        if (!startDate && !endDate) {
            return transformedMaintenanceData.tableData;
        }

        return transformedMaintenanceData.tableData.filter((item: any) => {
            if (!item.rawDueDate) return false;

            const itemDate = new Date(item.rawDueDate);
            const start = startDate ? new Date(startDate) : null;
            const end = endDate ? new Date(endDate) : null;

            if (start && end) {
                return itemDate >= start && itemDate <= end;
            } else if (start) {
                return itemDate >= start;
            } else if (end) {
                return itemDate <= end;
            }
            return true;
        });
    }, [transformedMaintenanceData.tableData, startDate, endDate]);

    // Apply date range filtering to work history table data
    const filteredWorkHistoryTableData = useMemo(() => {
        if (!startDate && !endDate) {
            return transformedWorkHistoryData.tableData;
        }

        return transformedWorkHistoryData.tableData.filter((item: any) => {
            if (!item.rawDate) return false;

            const itemDate = new Date(item.rawDate);
            const start = startDate ? new Date(startDate) : null;
            const end = endDate ? new Date(endDate) : null;

            if (start && end) {
                return itemDate >= start && itemDate <= end;
            } else if (start) {
                return itemDate >= start;
            } else if (end) {
                return itemDate <= end;
            }
            return true;
        });
    }, [transformedWorkHistoryData.tableData, startDate, endDate]);

    // Work history narrowed by the Job Type dropdown but not the date range -
    // the denominator for its "Showing X of Y Results" line
    const workHistoryByJobType = useMemo(() => {
        if (selectedJobType === 'All') {
            return transformedWorkHistoryData.tableData;
        }

        return transformedWorkHistoryData.tableData.filter(
            item => item.type === selectedJobType
        );
    }, [transformedWorkHistoryData.tableData, selectedJobType]);

    // Work history rows actually rendered - Job Type and date range applied
    const workHistoryRows = useMemo(() => {
        if (selectedJobType === 'All') {
            return filteredWorkHistoryTableData;
        }

        return filteredWorkHistoryTableData.filter(
            item => item.type === selectedJobType
        );
    }, [filteredWorkHistoryTableData, selectedJobType]);

    // Combine all transformed data. totalCount is the row count before the
    // date range is applied, so each table can report how much it is hiding.
    const data = useMemo(
        () => ({
            inspectionStatus: {
                ...filteredInspectionData,
                tableData: filteredInspectionTableData,
                totalCount: filteredInspectionData.tableData.length
            },
            maintenanceStatus: {
                ...transformedMaintenanceData,
                tableData: filteredMaintenanceTableData,
                totalCount: transformedMaintenanceData.tableData.length
            },
            workHistory: {
                tableData: workHistoryRows,
                totalCount: workHistoryByJobType.length
            }
        }),
        [
            filteredInspectionData,
            filteredInspectionTableData,
            transformedMaintenanceData,
            filteredMaintenanceTableData,
            workHistoryRows,
            workHistoryByJobType
        ]
    );

    // The results count only appears while the date range is narrowing things
    const isDateFiltered = Boolean(startDate || endDate);

    // Inspection Status Table Columns
    const inspectionColumns: Column<InspectionStatusData>[] = [
        {
            Header: t('dashboardAnalytics:serviceGrid.jobName'),
            accessor: 'jobName',
            minWidth: 150
        },
        {
            Header: t('dashboardAnalytics:serviceGrid.facility'),
            accessor: 'facility',
            minWidth: 150
        },
        {
            Header: t('dashboardAnalytics:serviceGrid.date'),
            accessor: 'date',
            width: 100
        },
        {
            Header: t('dashboardAnalytics:serviceGrid.inspected'),
            accessor: 'inspected',
            width: 100,
            Cell: (row: any) => {
                // If inspected is null, show "Pending" or dash
                if (row.value === null || row.value === undefined) {
                    return '-';
                }
                return `${row.value} %`;
            }
        },
        {
            Header: t('dashboardAnalytics:serviceGrid.pass'),
            accessor: 'pass',
            width: 100,
            Cell: (row: any) => {
                // If pass is null (job not closed), show dash
                if (row.value === null || row.value === undefined) {
                    return '-';
                }
                return `${row.value} %`;
            }
        },
        {
            Header: t('dashboardAnalytics:serviceGrid.report'),
            accessor: 'report',
            width: 100,
            Cell: (row: any) => {
                const hasDocument = row.original.documentID;
                return (
                    <Button
                        className="view-button"
                        disabled={!hasDocument}
                        onClick={() => {
                            if (hasDocument) {
                                const document: Partial<IcloudDocument> = {
                                    documentID: row.original.documentID,
                                    version: row.original.versionID
                                };
                                const result = downloadCloudDocument(
                                    t,
                                    document,
                                    false
                                ) as any;
                                if (
                                    result &&
                                    typeof result.then === 'function'
                                ) {
                                    result.then((blobUrl: string) => {
                                        if (blobUrl) {
                                            window.open(blobUrl, '_blank');
                                        }
                                    });
                                }
                            }
                        }}
                    >
                        View
                        {svgIcons.arrowUpRight}
                    </Button>
                );
            }
        }
    ];

    // Maintenance Status Table Columns
    const maintenanceColumns: Column<MaintenanceStatusData>[] = [
        {
            Header: t('dashboardAnalytics:serviceGrid.maintenanceType'),
            accessor: 'maintenanceType',
            minWidth: 80
        },
        {
            Header: t('dashboardAnalytics:serviceGrid.asset'),
            accessor: 'asset',
            minWidth: 280,
            Cell: (row: any) => <span className="asset-cell">{row.value}</span>
        },
        {
            Header: t('dashboardAnalytics:serviceGrid.status'),
            accessor: 'status',
            width: 100,
            className: 'status-cell',
            Cell: (row: any) => {
                const statusClass = row.value.replace(' ', '-').toLowerCase();
                return (
                    <span className={`status-badge status-${statusClass}`}>
                        {row.value === 'ok' && (
                            <span
                                className="status-dot"
                                style={{ backgroundColor: '#4CAF50' }}
                            />
                        )}
                        {row.value === 'due-soon' && (
                            <span
                                className="status-dot"
                                style={{ backgroundColor: '#FF9800' }}
                            />
                        )}
                        {row.value === 'due' && (
                            <span
                                className="status-dot"
                                style={{ backgroundColor: '#F44336' }}
                            />
                        )}
                        {row.value === 'snoozed' && (
                            <span
                                className="status-dot"
                                style={{ backgroundColor: '#9E9E9E' }}
                            />
                        )}
                    </span>
                );
            }
        },
        {
            Header: t('dashboardAnalytics:serviceGrid.dueDate'),
            accessor: 'dueDate',
            width: 120,
            className: 'due-date-cell'
        }
    ];

    // Work History Table Columns
    const workHistoryColumns: Column<WorkHistoryData>[] = [
        {
            Header: t('dashboardAnalytics:serviceGrid.jobName'),
            accessor: 'jobName',
            minWidth: 150
        },
        {
            Header: t('dashboardAnalytics:serviceGrid.workOrderNumber'),
            accessor: 'workOrderNumber',
            minWidth: 150
        },
        {
            Header: t('dashboardAnalytics:serviceGrid.date'),
            accessor: 'date',
            width: 100
        },
        {
            Header: t('dashboardAnalytics:serviceGrid.type'),
            accessor: 'type',
            width: 150
        },
        {
            Header: t('dashboardAnalytics:serviceGrid.report'),
            accessor: 'report',
            width: 100,
            Cell: (row: any) => {
                const hasDocument = row.original.documentID;
                return (
                    <Button
                        className="view-button"
                        disabled={!hasDocument}
                        onClick={() => {
                            if (hasDocument) {
                                const document: Partial<IcloudDocument> = {
                                    documentID: row.original.documentID,
                                    version: row.original.versionID
                                };
                                const result = downloadCloudDocument(
                                    t,
                                    document,
                                    false
                                ) as any;
                                if (
                                    result &&
                                    typeof result.then === 'function'
                                ) {
                                    result.then((blobUrl: string) => {
                                        if (blobUrl) {
                                            window.open(blobUrl, '_blank');
                                        }
                                    });
                                }
                            }
                        }}
                    >
                        View
                        {svgIcons.arrowUpRight}
                    </Button>
                );
            }
        }
    ];

    // Generate category options from productInfo (all available categories)
    const categoryOptions = useMemo(() => {
        if (
            !productInfo ||
            !productInfo.mainCategoryOptions ||
            !Array.isArray(productInfo.mainCategoryOptions)
        ) {
            return [{ value: 'All', label: 'Category: All' }];
        }

        // Convert productInfo options to the format needed for FilterDropdown
        const options = productInfo.mainCategoryOptions.map(option => ({
            value: option.value?.toString() || '',
            label: option.label || ''
        }));

        // Add "All" option at the beginning
        return [{ value: 'All', label: 'Category: All' }, ...options];
    }, [productInfo]);

    // Generate status options from dashboard analytics inspection data
    const statusOptions = useMemo(() => {
        if (!dashboardData || !dashboardData.inspection) {
            return [{ value: 'All', label: 'Status: All' }];
        }

        // Get the selected inspection category data (or ALL category)
        let selectedInspection = dashboardData.inspection.find(
            item => item.mainCategoryID === selectedCategory
        );

        if (!selectedInspection && selectedCategory === 'All') {
            selectedInspection = dashboardData.inspection.find(
                item => item.total > 0
            );
        }

        if (!selectedInspection || !selectedInspection.data) {
            return [{ value: 'All', label: 'Status: All' }];
        }

        // Extract unique statuses from the inspection data
        const statusSet = new Set<string>();
        selectedInspection.data.forEach((item: any) => {
            if (item.name) {
                // Convert API status names to lowercase (Pass -> pass, Fail -> fail, etc.)
                const statusName =
                    item.name.charAt(0).toLowerCase() + item.name.slice(1);
                statusSet.add(statusName);
            }
        });

        // Convert to option format with colors
        const options = Array.from(statusSet).map(statusName => {
            const translatedLabel = t(`dashboardAnalytics:${statusName}`);
            const displayLabel = applyStatusLabelOverrides(translatedLabel);
            const color =
                constants.installBaseStatusColorLookup[
                    translatedLabel as keyof typeof constants.installBaseStatusColorLookup
                ] || constants.colors.greyBlue;
            return {
                value: statusName,
                label: displayLabel,
                color
            };
        });

        // Add "All" option at the beginning
        return [{ value: 'All', label: 'Status: All' }, ...options];
    }, [dashboardData, selectedCategory, t]);

    // Search bar handlers
    const handleBackClick = () => {
        history.goBack();
    };

    const handleStartDateChange = (date: string) => {
        setStartDate(date);
    };

    const handleEndDateChange = (date: string) => {
        setEndDate(date);
    };

    const handleCategoryChange = (category: string) => {
        setSelectedCategory(category);
        // TODO: Implement category filtering
    };

    const handleStatusChange = (status: string) => {
        setSelectedStatus(status);
        // TODO: Implement status filtering
    };

    const handleBuildingChange = (building: string) => {
        setSelectedBuilding(building);
        // TODO: Implement building filtering
    };

    const handleFloorChange = (floor: string) => {
        setSelectedFloor(floor);
        // TODO: Implement floor filtering
    };

    const handleZoneChange = (zone: string) => {
        setSelectedZone(zone);
        // TODO: Implement zone filtering
    };

    const handleClearFilters = () => {
        setStartDate('');
        setEndDate('');
        setSelectedCategory('All');
        setSelectedStatus('All');
        setSelectedBuilding('All');
        setSelectedFloor('All');
        setSelectedZone('All');
    };

    // Print modal handlers
    const togglePrintModal = () => {
        setShowPrintModal(!showPrintModal);
    };

    const handlePrint = (date: moment.Moment) => {
        runReport(selectedFacility.id, date);
    };

    // Check if we have data to display. These look at the counts before the
    // date range is applied - a range that happens to exclude every row means
    // an empty table with its results count, not "no data for this facility".
    const hasInspectionData = data.inspectionStatus.totalAssets > 0;
    const hasMaintenanceData = data.maintenanceStatus.totalCount > 0;
    const hasWorkHistoryData = data.workHistory.totalCount > 0;

    // Show loading spinner while data is being fetched for a new facility or when filtering
    if (isLoading && (isFetchingNewFacility || isFilteringData)) {
        return (
            <>
                <ServiceSearchBar
                    onBackClick={handleBackClick}
                    startDate={startDate}
                    endDate={endDate}
                    onStartDateChange={handleStartDateChange}
                    onEndDateChange={handleEndDateChange}
                    onClearFilters={handleClearFilters}
                    loading={isLoading}
                />
                <div className="service-grid-container">
                    <div className="loading-container">
                        <Oval
                            color="#ff9738"
                            secondaryColor="#ffcd9e"
                            height={80}
                            width={80}
                        />
                    </div>
                </div>
            </>
        );
    }

    // Show message if no facility is selected
    if (!selectedFacilityID) {
        return (
            <>
                <ServiceSearchBar
                    onBackClick={handleBackClick}
                    startDate={startDate}
                    endDate={endDate}
                    onStartDateChange={handleStartDateChange}
                    onEndDateChange={handleEndDateChange}
                    onClearFilters={handleClearFilters}
                    loading={isLoading}
                />
                <div className="service-grid-container">
                    <div
                        className="no-data-message"
                        style={{ padding: '40px 20px', textAlign: 'center' }}
                    >
                        <h3>No Facility Selected</h3>
                        <p>
                            Please select a facility to view service grid data.
                        </p>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            {/* Search Bar */}
            <ServiceSearchBar
                onBackClick={handleBackClick}
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={handleStartDateChange}
                onEndDateChange={handleEndDateChange}
                onClearFilters={handleClearFilters}
                loading={isLoading}
            />

            <div className="service-grid-container">
                {/* Inspection Status Section */}
                <div className="service-grid-section">
                    <div className="section-header">
                        <h2>Inspection Status</h2>
                        {hasInspectionData && (
                            <Button
                                bsStyle="link"
                                className="collapse-button"
                                onClick={() =>
                                    setIsInspectionCollapsed(
                                        !isInspectionCollapsed
                                    )
                                }
                            >
                                {isInspectionCollapsed ? 'Expand' : 'Collapse'}
                                <FontAwesomeIcon
                                    icon={
                                        isInspectionCollapsed
                                            ? ['fas', 'chevron-down']
                                            : ['fas', 'chevron-up']
                                    }
                                    className="collapse-icon"
                                />
                            </Button>
                        )}
                    </div>
                    {!hasInspectionData ? (
                        <div className="no-data-message">
                            <p>
                                No inspection status data available for this
                                facility.
                            </p>
                        </div>
                    ) : !isInspectionCollapsed ? (
                        <>
                            {data.inspectionStatus.statusBar.length > 0 && (
                                <StatusCard
                                    title={t(
                                        'dashboardAnalytics:serviceGrid.inspectionStatus'
                                    )}
                                    subtitle={`${data.inspectionStatus.totalAssets} Assets`}
                                    statusData={data.inspectionStatus.statusBar}
                                    showLegendInFooter={true}
                                    actionButtons={
                                        <Button
                                            className="print-button"
                                            onClick={togglePrintModal}
                                        >
                                            <FontAwesomeIcon icon={faPrint} />
                                            <span>
                                                {t(
                                                    'dashboardAnalytics:printUpdatedList'
                                                )}
                                            </span>
                                        </Button>
                                    }
                                />
                            )}

                            <ServiceGridTable
                                data={data.inspectionStatus.tableData}
                                columns={inspectionColumns}
                                totalCount={data.inspectionStatus.totalCount}
                                showCount={isDateFiltered}
                                noDataText="No inspection data available"
                            />
                        </>
                    ) : null}
                </div>

                {/* Maintenance Actions Section */}
                <div className="service-grid-section">
                    <div className="section-header">
                        <h2>Maintenance Actions</h2>
                        {hasMaintenanceData && (
                            <Button
                                bsStyle="link"
                                className="collapse-button"
                                onClick={() =>
                                    setIsMaintenanceCollapsed(
                                        !isMaintenanceCollapsed
                                    )
                                }
                            >
                                {isMaintenanceCollapsed ? 'Expand' : 'Collapse'}
                                <FontAwesomeIcon
                                    icon={
                                        isMaintenanceCollapsed
                                            ? ['fas', 'chevron-down']
                                            : ['fas', 'chevron-up']
                                    }
                                    className="collapse-icon"
                                />
                            </Button>
                        )}
                    </div>
                    {!hasMaintenanceData ? (
                        <div className="no-data-message">
                            <p>
                                No maintenance actions data available for this
                                facility.
                            </p>
                        </div>
                    ) : !isMaintenanceCollapsed ? (
                        <>
                            {data.maintenanceStatus.statusBar.length > 0 && (
                                <StatusCard
                                    title={t(
                                        'dashboardAnalytics:serviceGrid.maintenanceActions'
                                    )}
                                    subtitle={t(
                                        'dashboardAnalytics:serviceGrid.totalActions',
                                        {
                                            count:
                                                data.maintenanceStatus
                                                    .totalActions
                                        }
                                    )}
                                    statusData={
                                        data.maintenanceStatus.statusBar
                                    }
                                    showLegendInFooter={true}
                                    actionButtons={
                                        <Button
                                            className="print-button"
                                            onClick={() => {
                                                setWorkOrderTableFilter({
                                                    tabKey:
                                                        workOrderTabEnum.preventative
                                                });
                                                history.push('/workorders');
                                            }}
                                        >
                                            <FontAwesomeIcon
                                                icon={['far', 'wrench']}
                                            />
                                            <span>
                                                {t(
                                                    'dashboardAnalytics:serviceGrid.setupMaintenance'
                                                )}
                                            </span>
                                        </Button>
                                    }
                                />
                            )}
                            <ServiceGridTable
                                data={data.maintenanceStatus.tableData}
                                columns={maintenanceColumns}
                                totalCount={data.maintenanceStatus.totalCount}
                                showCount={isDateFiltered}
                                noDataText="No maintenance data available"
                            />
                        </>
                    ) : null}
                </div>

                {/* Work History Section */}
                <div className="service-grid-section">
                    <div className="section-header">
                        <h2>Work History</h2>
                        {hasWorkHistoryData && (
                            <Button
                                bsStyle="link"
                                className="collapse-button"
                                onClick={() =>
                                    setIsWorkHistoryCollapsed(
                                        !isWorkHistoryCollapsed
                                    )
                                }
                            >
                                {isWorkHistoryCollapsed ? 'Expand' : 'Collapse'}
                                <FontAwesomeIcon
                                    icon={
                                        isWorkHistoryCollapsed
                                            ? ['fas', 'chevron-down']
                                            : ['fas', 'chevron-up']
                                    }
                                    className="collapse-icon"
                                />
                            </Button>
                        )}
                    </div>
                    {!hasWorkHistoryData ? (
                        <div className="no-data-message">
                            <p>
                                No work history data available for this
                                facility.
                            </p>
                        </div>
                    ) : !isWorkHistoryCollapsed ? (
                        <>
                            <div className="status-card work-history-card">
                                <div className="status-card-header">
                                    <div className="work-history-controls">
                                        <select
                                            className="job-type-filter"
                                            value={selectedJobType}
                                            onChange={e =>
                                                setSelectedJobType(
                                                    e.target.value
                                                )
                                            }
                                        >
                                            <option value="All">
                                                {t(
                                                    'dashboardAnalytics:serviceGrid.jobType'
                                                )}
                                                : All
                                            </option>
                                            {jobTypeFilterOptions.map(
                                                jobType => (
                                                    <option
                                                        key={jobType}
                                                        value={jobType}
                                                    >
                                                        {jobType}
                                                    </option>
                                                )
                                            )}
                                        </select>
                                    </div>
                                    <Button
                                        className="print-button"
                                        onClick={() =>
                                            history.push('/documents')
                                        }
                                    >
                                        <FontAwesomeIcon
                                            icon={['far', 'folder']}
                                        />
                                        <span>
                                            {t(
                                                'dashboardAnalytics:serviceGrid.fullDocumentLibrary'
                                            )}
                                        </span>
                                    </Button>
                                </div>
                                <ServiceGridTable
                                    data={data.workHistory.tableData}
                                    columns={workHistoryColumns}
                                    totalCount={data.workHistory.totalCount}
                                    showCount={isDateFiltered}
                                    noDataText="No work history available"
                                />
                            </div>
                        </>
                    ) : null}
                </div>
            </div>
            <PrintUpdatedListModal
                show={showPrintModal}
                onHide={togglePrintModal}
                onPrint={handlePrint}
                title={t('dashboardAnalytics:printListModalTitle')}
                className="print-updated-list-modal"
            />
        </>
    );
};

const mapStateToProps = (state: any) => {
    const selectedFacility = selectSelectedFacilityWithBuildings(state);
    return {
        selectedFacilityID: state.selectedFacilityID,
        selectedFacilityName: selectedFacility?.name || '',
        selectedFacility,
        dashboardData: state.dashboardAnalytics.data,
        inspectionStatus: state.dashboardAnalytics.inspectionStatus,
        maintenanceActions: state.dashboardAnalytics.maintenanceActions,
        workHistory: state.dashboardAnalytics.workHistory,
        productInfo: state.productInfo,
        isLoading: selectIsLoading(state)
    };
};

const mapDispatchToProps = {
    getDashboardAnalytics,
    getDashboardInspectionStatus,
    getDashboardMaintenanceActions,
    getDashboardWorkHistory,
    getFacility,
    setTableFilter,
    getCloudDocument,
    runCodeDeficiencyReport,
    getProductInfo
};

export default connect(mapStateToProps, mapDispatchToProps)(ServiceGrid);
